import React, { useState } from 'react';

export interface IModalProviderProps {
    /**
     * Content.
     */
    children: React.ReactNode;
    /**
     * ID attribute.
     */
    id?: string;
    /**
     * Should modal be initially shown. Won't work with isOpen and onToggle.
     */
    defaultIsOpen?: boolean;
    /**
     * Should the modal be open or closed.
     * Use to handle state outside of component, should use with onToggle prop.
     */
    isOpen?: boolean;
    /**
     * Callback when modal is toggled.
     * Use to handle state outside of component, should use with isOpen prop.
     */
    onToggle?: (isOpen: boolean) => void;
    /**
     * Callback for when the user opens the modal (via modal-trigger).
     */
    onOpen?: () => void;
    /**
     * Callback for when the user closes the modal (via close button, backdrop click or ESC key).
     */
    onClose?: () => void;
    /**
     * If modal is closable with modal closer button.
     */
    isClosable?: boolean;
}

export type TModalCloseEvent = React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;

export interface IModalContext {
    isOpen: boolean;
    id?: string;
    open?: () => void;
    close?: (event: TModalCloseEvent, childOnClick?: (e: TModalCloseEvent) => void) => void;
    isClosable?: boolean;
}

export const ModalContext = React.createContext<IModalContext>({
    isClosable: true,
    isOpen: false,
});

export const ModalProvider = ({
    children,
    defaultIsOpen = false,
    isClosable = true,
    isOpen: propsIsOpen,
    onToggle,
    onOpen,
    onClose,
    id,
}: IModalProviderProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen);
    const open: boolean = (onToggle || (onOpen && onClose)) && typeof propsIsOpen !== 'undefined' ? propsIsOpen : isOpen;

    const openModal = (): void => {
        if (typeof propsIsOpen === 'undefined') {
            setIsOpen(true);
        }

        if (onToggle) {
            onToggle(true);
        }

        if (onOpen) {
            onOpen();
        }
    };

    const closeModal = (event: TModalCloseEvent, childOnClick?: (e: TModalCloseEvent) => void): void => {
        if (typeof propsIsOpen === 'undefined') {
            setIsOpen(false);
        }

        if (onToggle) {
            onToggle(false);
        }

        if (onClose) {
            onClose();
        }

        if (childOnClick) {
            childOnClick(event);
        }
    };

    const modalContextValue: IModalContext = {
        close: closeModal,
        id,
        isClosable,
        isOpen: open,
        open: openModal,
    };

    return (
        <ModalContext.Provider value={modalContextValue}>
            {children}
        </ModalContext.Provider>
    );
};

